import { ColumnValue } from 'components/filters/TableFilter/TableFilter';
import { DateParams } from 'utils/filterUtils';
import {
  FinancialReportFieldFilterModel,
  FinancialReportSortFieldEnum,
} from 'api/backofficeApiClient';

export enum FinancialReportFilterTypes {
  Date = 'date',
  Columns = 'columns',
}

export type FinancialReportMultipleFilterValues = Record<
  FinancialReportFilterFieldEnum,
  ColumnValue[]
>;

export type FinancialReportColumnsValues = Record<
  FinancialReportFilterFieldEnum,
  string[]
>;

// We use this type, because we don't need date and parentVendor fields
export type FinancialReportFilterFieldEnum = Exclude<
  FinancialReportSortFieldEnum,
  FinancialReportSortFieldEnum.Date
>;

export type FinancialReportFilters = {
  [FinancialReportFilterTypes.Date]: DateParams | undefined;
  [FinancialReportFilterTypes.Columns]: FinancialReportColumnsValues;
};

const getFormattedValuesForFilterMultiple = (
  filterDetails: FinancialReportFieldFilterModel[],
): FinancialReportMultipleFilterValues => {
  const result = filterDetails.map(
    (filterDetail: FinancialReportFieldFilterModel) => {
      return {
        [filterDetail.fieldType as FinancialReportFilterFieldEnum]: (
          filterDetail.values || []
        ).map((columnValue) => {
          return {
            id: columnValue,
            value: columnValue,
            checked: false,
          };
        }),
      };
    },
  );
  return Object.assign({}, ...result);
};

const mapFinancialReportFiltersToRequestModel = (
  filters: FinancialReportColumnsValues,
): FinancialReportFieldFilterModel[] => {
  return Object.entries(filters)
    .filter((columnValue) => columnValue[1].length)
    .map((columnValue) => {
      return {
        fieldType: +columnValue[0],
        values: columnValue[1],
      };
    });
};

const defaultFilterValues: FinancialReportFilters = {
  [FinancialReportFilterTypes.Date]: undefined,
  [FinancialReportFilterTypes.Columns]: {
    [FinancialReportSortFieldEnum.CustomerName]: [],
    [FinancialReportSortFieldEnum.Member1Name]: [],
    [FinancialReportSortFieldEnum.Member2Name]: [],
    [FinancialReportSortFieldEnum.WorkerTypeWorkerID]: [],
    [FinancialReportSortFieldEnum.ParentVendor]: [],
    [FinancialReportSortFieldEnum.WorkerName]: [],
    [FinancialReportSortFieldEnum.WorkerJobTitle]: [],
    [FinancialReportSortFieldEnum.CommissionDetails]: [],
    [FinancialReportSortFieldEnum.MemberMarkup]: [],
    [FinancialReportSortFieldEnum.BillHoursReg]: [],
    [FinancialReportSortFieldEnum.BillHoursOT]: [],
    [FinancialReportSortFieldEnum.BillRateReg]: [],
    [FinancialReportSortFieldEnum.BillRateOT]: [],
    [FinancialReportSortFieldEnum.BillAmount]: [],
    [FinancialReportSortFieldEnum.PayHoursReg]: [],
    [FinancialReportSortFieldEnum.PayHoursOT]: [],
    [FinancialReportSortFieldEnum.PayRateReg]: [],
    [FinancialReportSortFieldEnum.PayRateOT]: [],
    [FinancialReportSortFieldEnum.PayAmount]: [],
    [FinancialReportSortFieldEnum.MBPRate]: [],
    [FinancialReportSortFieldEnum.MBPFee]: [],
    [FinancialReportSortFieldEnum.MSPFee]: [],
    [FinancialReportSortFieldEnum.VMSFee]: [],
    [FinancialReportSortFieldEnum.TotalProfit]: [],
    [FinancialReportSortFieldEnum.Member1USDHr]: [],
    [FinancialReportSortFieldEnum.Member2USDHr]: [],
    [FinancialReportSortFieldEnum.Member1ProfitPercent]: [],
    [FinancialReportSortFieldEnum.Member2ProfitPercent]: [],
    [FinancialReportSortFieldEnum.Member1Profit]: [],
    [FinancialReportSortFieldEnum.Member2Profit]: [],
    [FinancialReportSortFieldEnum.InvoiceNumber]: [],
  },
};

const closureCounter = () => {
  let index = 0;
  return () => {
    return index++;
  };
};

export {
  getFormattedValuesForFilterMultiple,
  mapFinancialReportFiltersToRequestModel,
  defaultFilterValues,
  closureCounter,
};
